import { POST, stringifyPermission } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import type { PermissionParsed } from 'types/Permission.ts';
import type { User } from 'types/User.ts';
import { Button } from 'ui/component/Button.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Input } from 'ui/control/Input.tsx';
import { Select } from 'ui/control/Select.tsx';
import { SelectMulti } from 'ui/control/SelectMulti.tsx';
import { SwitchResourceModeButton } from '#admin/component/SwitchResourceModeButton.tsx';

type RolePermissionFormProps = {
  onAdd: (values: PermissionParsed & { unparsed_permission: string }) => void;
  onAddLoading?: boolean;
};

export const AddRolePermissionForm = ({ onAdd, onAddLoading }: RolePermissionFormProps) => {
  const { t } = useTranslation();
  const [resourceMode, setResourceMode] = React.useState<'select' | 'text'>('select');

  const { data: users } = useSWR<User[]>(['/user/list', { is_group: true, sort: [['name', 'asc']] }], POST);
  const { data: operations } = useSWR<string[]>(['/permission/list-operations', {}], POST);

  // roles can be created only for groups
  const availableSubjects = ['UserID/*'];

  const availableResources = React.useMemo(() => {
    if (!users) return {};

    const mappedUsers: Record<string, string> = {};
    for (const user of users) {
      mappedUsers[user.id + (user.is_group ? '/*' : '')] = user.name + (user.is_group ? '/*' : '');
    }

    return {
      '*': '*',
      UserID: 'UserID',
      OwnerID: 'OwnerID',
      'UserID/*': 'UserID/*',
      'OwnerID/*': 'OwnerID/*',
      'OwnerID/OwnerID/*': 'OwnerID/OwnerID/*',
      ...mappedUsers,
    };
  }, [users]);

  const formRef = React.useRef<HTMLFormElement>(null);
  const formContext = useForm<PermissionParsed>({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: {
      subject: 'UserID/*',
      subject_is_group: true,
      operation: '*',
      resource: 'UserID/*',
      resource_is_owner: true,
    },
  });
  const { handleSubmit, reset } = formContext;

  const onSubmit = async (values: PermissionParsed) => {
    void onAdd({ ...values, unparsed_permission: stringifyPermission(values) });
    reset();
  };

  if (!operations) return <Spinner />;

  return (
    <FormProvider {...formContext}>
      <form id="search-form" ref={formRef} className="mt-4 mb-8 flex flex-col gap-8" noValidate={true} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <div className="bg-blue-sky p-4 px-6">
          <div className="flex items-end gap-4">
            <Select
              className="w-[240px]"
              name={'subject'}
              label={t('forms.role.permission.subject.label')}
              placeholder={t('forms.role.permission.subject.placeholder')}
              required={true}
              autoComplete={'off'}
              options={availableSubjects}
              registerOptions={{ required: true }}
            />
            <SelectMulti
              className="w-[240px]"
              name={'operation'}
              label={t('forms.role.permission.operation.label')}
              options={operations}
              placeholder={t('forms.role.permission.operation.placeholder')}
              selectable={'one'}
              closeOnChangedValue={true}
              hasClearAll={true}
              required={true}
            />
            {resourceMode === 'select' ? (
              <SelectMulti
                className="w-[240px]"
                name={'resource'}
                label={
                  <SwitchResourceModeButton
                    text={t('forms.role.permission.resource.label')}
                    resourceMode={resourceMode}
                    onClick={() => setResourceMode((prevMode) => (prevMode === 'select' ? 'text' : 'select'))}
                  />
                }
                options={availableResources}
                placeholder={t('forms.role.permission.resource.placeholder.select')}
                selectable={'one'}
                closeOnChangedValue={true}
                hasClearAll={true}
                required={true}
              />
            ) : (
              <Input
                className="w-[240px]"
                name={'resource'}
                label={
                  <SwitchResourceModeButton
                    text={t('forms.role.permission.resource.label')}
                    resourceMode={resourceMode}
                    onClick={() => setResourceMode((prevMode) => (prevMode === 'select' ? 'text' : 'select'))}
                  />
                }
                placeholder={t('forms.role.permission.resource.placeholder.text')}
                required={true}
                autoComplete={'off'}
                registerOptions={{ required: true }}
              />
            )}
            <Button className="blue h-[37px] max-w-60" type="submit" disabled={onAddLoading} loading={onAddLoading}>
              {t('forms.role.permission.buttons.add')}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
